import { useEffect, useRef, useState } from "react";
import { Manager, type Socket } from "socket.io-client";
import { getTransporterApiBaseUrl } from "./api-base";

const MAX_EVENTS = 24;

/** Unicode dash lookalikes break server UUID path regex → **Invalid namespace**. */
function normalizeWsId(raw: string): string {
  return raw.trim().replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, "-");
}

export type TransporterWsFeedItem = { at: string; event: string; preview: string };

/**
 * **§15.3** — Socket.io **`/ws/transporter/:transporterId`** with Clerk JWT in **`auth.token`**.
 *
 * Uses **`Manager` + explicit namespace** so Engine.IO hits **`/` + path** only (`/socket.io/…` on the origin).
 * The **`io(fullUrlIncludingNamespace)`** shortcut can mis-parse or multiplex oddly behind Vite proxy and surface
 * **`Invalid namespace`** even when the server regex is correct.
 */
export function useTransporterSocket(
  transporterId: string | null,
  getToken: () => Promise<string | null>,
  enabled: boolean,
  onNamedEvent?: (eventName: string) => void,
): { connected: boolean; lastError: string | null; feed: TransporterWsFeedItem[]; socket: Socket | null } {
  const [connected, setConnected] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [feed, setFeed] = useState<TransporterWsFeedItem[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const onNamedEventRef = useRef(onNamedEvent);
  onNamedEventRef.current = onNamedEvent;

  useEffect(() => {
    const id = transporterId ? normalizeWsId(transporterId) : null;
    if (!enabled || !id) {
      setConnected(false);
      return;
    }

    // Single source of truth for this effect's socket lifecycle: this `let` variable. The cleanup
    // closure captures it, and `setSocket` is just a derived view of it for downstream consumers
    // (the context provider). Don't keep a separate ref — two write sites for the same object
    // invites a future bug where someone updates one and forgets the other.
    let activeSocket: Socket | null = null;
    let cancelled = false;

    void (async () => {
      const token = await getTokenRef.current();
      if (cancelled || !token) {
        if (!cancelled && !token) setLastError("missing_clerk_token");
        return;
      }

      const origin = getTransporterApiBaseUrl() || globalThis.location?.origin || "";
      const base = origin.replace(/\/$/, "");
      const nsp = `/ws/transporter/${id}`;

      const manager = new Manager(base, {
        path: "/socket.io/",
        autoConnect: false,
        transports: ["websocket", "polling"],
        reconnectionAttempts: 8,
        reconnectionDelay: 1200,
      });
      const socket = manager.socket(nsp, { auth: { token } });
      if (cancelled) {
        socket.disconnect();
        return;
      }
      activeSocket = socket;
      setSocket(socket);
      setLastError(null);

      socket.on("connect", () => {
        if (!cancelled) setConnected(true);
      });
      socket.on("disconnect", () => {
        if (!cancelled) setConnected(false);
      });
      socket.on("connect_error", (err: Error) => {
        if (!cancelled) {
          setConnected(false);
          setLastError(err?.message ?? "connect_error");
        }
      });

      socket.onAny((event, ...args) => {
        if (cancelled || event === "connect" || event === "disconnect") return;
        onNamedEventRef.current?.(event);
        let preview = "";
        try {
          preview =
            typeof args[0] === "string"
              ? args[0].slice(0, 200)
              : JSON.stringify(args[0]).slice(0, 200);
        } catch {
          preview = "";
        }
        setFeed((prev) => {
          const next = [
            { at: new Date().toISOString(), event, preview },
            ...prev,
          ].slice(0, MAX_EVENTS);
          return next;
        });
      });

      socket.connect();
    })();

    return () => {
      cancelled = true;
      if (activeSocket) {
        activeSocket.removeAllListeners();
        activeSocket.disconnect();
        activeSocket = null;
      }
      setSocket(null);
      setConnected(false);
    };
  }, [enabled, transporterId]);

  return { connected, lastError, feed, socket };
}
